import { useState, useEffect, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Card, CardHeader, Form, FormItem, Label, Input, Select, Option, Button, MessageStrip, FlexBox, Text } from '@ui5/webcomponents-react';
import { api } from '../api/client';
import { useAuth } from '../store/auth';
import { detectChannel, apiBase } from '../platform';

export function LoginPage() {
  const qc = useQueryClient();
  const setSession = useAuth(s => s.setSession);
  const [userName, setUser] = useState('');
  const [password, setPass] = useState('');
  const [companies, setCompanies] = useState<string[]>([]);
  const [companyDb, setCompanyDb] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const channel = detectChannel();
  // Desktop/mobile talk to a remote gateway, so the server URL must be configurable *before* login.
  const [server, setServer] = useState(() => (channel === 'web' ? '' : apiBase()));

  // Load the selectable company databases (public endpoint). A single company hides the picker.
  const loadCompanies = () => api.companies()
    .then(r => { setCompanies(r.companies); setCompanyDb(r.default || r.companies[0] || ''); })
    .catch(() => { setCompanies([]); /* server may be single-tenant/offline; login still works with the default */ });
  useEffect(() => { loadCompanies(); }, []);

  // Persist the gateway URL (used by apiBase() on the next request) and refresh the company list.
  const commitServer = () => {
    const n = server.trim().replace(/\/$/, '');
    if (n) localStorage.setItem('b1links.apiBase', n); else localStorage.removeItem('b1links.apiBase');
    loadCompanies();
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault(); setBusy(true); setError(null);
    try {
      const r = await api.login(userName, password, companyDb || undefined);
      qc.clear(); // drop any cache from a previous session so this one loads the full menu/data fresh
      setSession(r.token, r.principal);
    }
    catch (err) { setError((err as Error).message); }
    finally { setBusy(false); }
  };

  return (
    <FlexBox justifyContent="Center" alignItems="Center" style={{ height: '100%', padding: '1rem' }}>
      <Card header={<CardHeader titleText="B1Links" subtitleText={`Sign in · ${channel}`} />} style={{ width: 'min(420px, 100%)' }}>
        <form onSubmit={submit} style={{ padding: '1rem' }}>
          <Form layout="S1 M1 L1 XL1" labelSpan="S12 M12 L12 XL12">
            {channel !== 'web' && (
              <FormItem labelContent={<Label>Server URL</Label>}>
                <Input value={server} placeholder="https://www.ftzones.com/B1Links" onInput={e => setServer(e.target.value ?? '')} onChange={commitServer} style={{ width: '100%' }} />
              </FormItem>
            )}
            {companies.length > 1 && (
              <FormItem labelContent={<Label required>Company</Label>}>
                <Select style={{ width: '100%' }} onChange={e => setCompanyDb(e.detail.selectedOption.dataset.db ?? '')}>
                  {companies.map(db => <Option key={db} data-db={db} selected={db === companyDb}>{db}</Option>)}
                </Select>
              </FormItem>
            )}
            <FormItem labelContent={<Label required>User</Label>}>
              <Input value={userName} onInput={e => setUser(e.target.value ?? '')} style={{ width: '100%' }} />
            </FormItem>
            <FormItem labelContent={<Label required>Password</Label>}>
              <Input type="Password" value={password} onInput={e => setPass(e.target.value ?? '')} style={{ width: '100%' }} />
            </FormItem>
          </Form>
          {error && <MessageStrip design="Negative" hideCloseButton style={{ marginTop: '0.5rem' }}>{error}</MessageStrip>}
          <FlexBox justifyContent="End" style={{ marginTop: '1rem' }}>
            <Button design="Emphasized" type="Submit" disabled={busy || !userName || !password}>{busy ? 'Signing in…' : 'Sign in'}</Button>
          </FlexBox>
          <Text style={{ display: 'block', marginTop: '0.75rem', color: 'var(--sapNeutralTextColor)' }}>
            Desktop uses your SAP Business One user; web and mobile use your B1Links app user unless configured otherwise.
            {channel === 'mobile' && ' Server URL is the gateway address, e.g. https://www.ftzones.com/B1Links.'}
          </Text>
        </form>
      </Card>
    </FlexBox>
  );
}
